import {makeAutoObservable, runInAction} from "mobx";
import {CHIP_COLORS} from "../../../../components/chip/chip";
import {FEATURES} from "../../../../models/Plan";
import RmaService from "../../../../services/api/rma-service";
import {rootContainer, TYPE} from "../../../../services/container/root";
import OrderService from "../../../../services/order-service";
import OrderStore from "../../../create-rma/stores/order-store";
import {RMA_STATUS_TRANSLATION_KEY} from "../../../../services/api/rma-list-service";
import {
    DRAFT_ORDER_EXCHANGE_TYPES,
    DRAFT_ORDER_STATE,
    IRma,
    IRmaBulkRmaAction,
    IRmaData,
    IRMAEmailLog,
    IRMAReturnMethodPreset,
    IRmaSettings,
    RMA_STATUS,
    IValidateItemsActionRequest,
    IStoreLocation,
    REFUND_LEVEL,
    SHIPMENT_STATUS_ENUM,
    IReceiveShipmentRequest,
    IRmaRejectRequest,
    IRmaReopenReuest,
    IRmaCancelRequest,
    IRestockRequest,
    IRmaApproveRequest,
    IRmaRejectItemRequest,
} from "../rma-types";

import CustomerCaseStore from "./customer-case-store";
import RmaShipmentStore from "./rma-shipment-store";
import { IOption } from "../../../../components/inputs/select-dropdown/select-dropdown";
import { IReturnMethod, RETURN_METHOD_TYPE } from "../../../return-methods/return-method-types";
import currentPortalStore from "../../../../stores/current-portal-store";
import RefundCalculatorStore from "./refund-calculator-store";
import { RmaPageMainStore } from "./rma-page-main-store";
import ProductCaseStore from "./product-case-store";
import { GroupedOption } from "../../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import { DROP_OFF_LOCATION } from "../../../return-methods/constants";
import RefundCalculatorService from "../../../../services/api/refund-calculator-service";
import currentAccountStore from "../../../accounts/stores/current-account-store";
import { PERMISSION } from "../../../../models/PermissionsTypes";
import dashboardSettingsStore from "../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES, IGlobalERmaPayloadCapability } from "../../../../models/ProfileCapabilities";
import RmaErrorListStore from "./rma-error-list-store";
import customersMainStore from "../../../create-rma/stores/customers-main-store";
import TimelineManagerStore from "./timeline-manager-store";
import {IManagerCredentials} from "../../../../manager-approval-modal/manager-credentials-stores";
import AggregatorsInfoStore from "../../../return-methods/stores/aggregators-info-store";
import RmaApproveActionStore from "./rma-action-store/rma-approve-action-store";
import ResolveLabelErrorActionStore from "./rma-action-store/resolve-label-error-action-store";
import ReceiveShipmentActionStore from "./rma-action-store/receive-shipment-action-store";
import RmaRejectActionStore from "./rma-action-store/rma-reject-action-store";
import RmaReopenActionStore from "./rma-action-store/rma-reopen-action-store";
import RmaRestockActionStore from "./rma-action-store/rma-restock-action-store";
import RmaCancelActionStore from "./rma-action-store/rma-cancel-action-store";
import RmaActionStore from "./rma-action-store";
import RmaExchangeOrderStore from "./rma-exchange-order-store";
import RmaActionsToolbarStore from "./rma-actions-toolbar-store";
import RmaRejectItemActionStore from "./rma-action-store/rma-reject-item-action-store";


export enum RMA_ACTION_TYPE {
    APPROVE = 'approve',
    RECEIVE_SHIPMENT = 'receive-shipment',
    REOPEN = 'reopen',
    REJECT = 'reject',
    CANCEL = 'cancel',
    MARK_AS_DONE = 'mark-as-done',
    RELEASE_EXCHANGE = 'release-exchange',
    RESTOCK = 'restock',
    REFUND = 'refund',
    RESOLVE_ERRORS = 'resolve-errors',
    VALIDATE_ALL_ITEMS = 'validate-all-items',
    REJECT_ITEM = 'reject-item',
    RESOLVE_LABEL_ERROR = 'resolve-label-error',
}

interface IDropOffLocationAddress {
    source: DROP_OFF_LOCATION;
    name: string | null;
}

/**
 * Base fields shared by every RMA toolbar action entry.
 * The concrete variants below add a narrow `type` discriminant and a matching `store` class
 * so downstream components can access store-specific capabilities without type assertions.
 */
export interface IRmaActionBase {
    name: string;
    descriptionTranslationKey: string;
    buttonTextTranslationKey: string;
    modalTitleTranslationKey?: string;
    disabled?: boolean;
}

export interface IRmaApproveAction extends IRmaActionBase {
    type: RMA_ACTION_TYPE.APPROVE;
    store: RmaApproveActionStore;
}

export interface IRmaReceiveShipmentAction extends IRmaActionBase {
    type: RMA_ACTION_TYPE.RECEIVE_SHIPMENT;
    store: ReceiveShipmentActionStore;
}

export interface IRmaMarkAsDoneAction extends IRmaActionBase {
    type: RMA_ACTION_TYPE.MARK_AS_DONE;
    store: RmaActionStore;
}

export interface IRmaReopenAction extends IRmaActionBase {
    type: RMA_ACTION_TYPE.REOPEN;
    store: RmaReopenActionStore;
}

export interface IRmaRejectAction extends IRmaActionBase {
    type: RMA_ACTION_TYPE.REJECT;
    store: RmaRejectActionStore;
}

export interface IRmaRejectItemAction extends IRmaActionBase {
    type: RMA_ACTION_TYPE.REJECT_ITEM;
    store: RmaRejectItemActionStore;
}

export interface IRmaCancelAction extends IRmaActionBase {
    type: RMA_ACTION_TYPE.CANCEL;
    store: RmaCancelActionStore;
}

export interface IRmaReleaseExchangeAction extends IRmaActionBase {
    type: RMA_ACTION_TYPE.RELEASE_EXCHANGE;
    store: RmaActionStore;
}

export interface IRmaRestockAction extends IRmaActionBase {
    type: RMA_ACTION_TYPE.RESTOCK;
    store: RmaRestockActionStore;
}

export interface IRmaResolveErrorsAction extends IRmaActionBase {
    type: RMA_ACTION_TYPE.RESOLVE_ERRORS;
    store: RmaActionStore;
}

export interface IRmaResolveLabelErrorAction extends IRmaActionBase {
    type: RMA_ACTION_TYPE.RESOLVE_LABEL_ERROR;
    store: ResolveLabelErrorActionStore;
}

export interface IRmaRefundAction extends IRmaActionBase {
    type: RMA_ACTION_TYPE.REFUND;
    store: RmaActionStore;
}

export interface IRmaValidateAllAction extends IRmaActionBase {
    type: RMA_ACTION_TYPE.VALIDATE_ALL_ITEMS;
    store: RmaActionStore;
}

export type IRmaAction =
    | IRmaApproveAction
    | IRmaReceiveShipmentAction
    | IRmaMarkAsDoneAction
    | IRmaReopenAction
    | IRmaRejectAction
    | IRmaRejectItemAction
    | IRmaCancelAction
    | IRmaReleaseExchangeAction
    | IRmaRestockAction
    | IRmaResolveErrorsAction
    | IRmaResolveLabelErrorAction
    | IRmaRefundAction
    | IRmaValidateAllAction;

/** Maps each RMA action type discriminant to its matching action variant. */
type RmaActionByType = {
    [K in RMA_ACTION_TYPE]: Extract<IRmaAction, { type: K }>;
};

interface IProductCaseState {
    checked: boolean;
}
interface IProductCasesStateMap {
    [productCaseId: number]: IProductCaseState;
}


class RmaPageStore {
    private _rmaId: number;
    private _model?: IRma;
    private _settings?: IRmaSettings;
    private _shipmentList: RmaShipmentStore[] = [];
    private _customerCaseStore?: CustomerCaseStore;
    private _errorListStore?: RmaErrorListStore;
    private _orderStore?: OrderStore;
    private _isLoading: boolean = false;
    private _isOrderLoading: boolean = false;
    private _isResendingStatusEmail: boolean = false;
    private _isReleasingExchnage: boolean = false;
    private _isValidatingItems: boolean = false;
    private _timelineListManager?: TimelineManagerStore;
    private _emailLogList: IRMAEmailLog[] = [];
    private _newTimelineNote: string = '';
    private _displayNewTimelineNote: boolean = false;
    private _isSavingNewNote: boolean = false;
    private _refundCalculatorStore?: RefundCalculatorStore;
    private _isRefunding: boolean = false;
    private _managerCredentials?: IManagerCredentials
    private _product_cases_state_map: IProductCasesStateMap = {};
    private _aggregatorsInfoStore?: AggregatorsInfoStore;
    private _rmaAactions: RmaActionByType = {} as RmaActionByType;
    private _rmaActionsStore: RmaActionsToolbarStore;


	constructor(private _mainStore: RmaPageMainStore, id: number) {
        this._rmaId = id;
		makeAutoObservable(this, {}, { autoBind:true });
        this.constructRmaActions();
        this._rmaActionsStore = new RmaActionsToolbarStore();
	}

	private get _service(): RmaService { return rootContainer.get<RmaService>(TYPE.RmaService);}
	public get isLoading(): boolean { return this._isLoading || this._isOrderLoading || !!this._orderStore?.customer?.isLoading; }
	public get isResendingStatusEmail(): boolean { return this._isResendingStatusEmail; }
	public get isReleasingExchnage(): boolean { return this._isReleasingExchnage; }
    public get isSavingNewNote(): boolean {return this._isSavingNewNote; }
	public get shipmentList(): RmaShipmentStore[] { return this._shipmentList; }
	public get customerCaseStore(): CustomerCaseStore | undefined { return this._customerCaseStore; }
    public get productCasesStateMap(): IProductCasesStateMap { return this._product_cases_state_map; }
	public get errorListStore(): RmaErrorListStore | undefined { return this._errorListStore; }
    public get timelineListManager(): TimelineManagerStore | undefined { return this._timelineListManager; }
    public get emailLogList(): IRMAEmailLog[] { return this._emailLogList; }
    public get orderStore(): OrderStore | undefined { return this._orderStore; }
    public get aggregatorsStore(): AggregatorsInfoStore  | undefined { return this._aggregatorsInfoStore; }
    public get rmaId(): number { return this._rmaId; }

    /**
     * Records a download-label audit entry. Fire-and-forget so opening the label is never blocked.
     */
    public auditDownloadLabel(shipmentId: number): void {
        void this._service.auditDownloadLabel(this._rmaId, shipmentId).catch(() => {
            // Intentional: audit failures must not block label download UX (RGET-3112).
        });
    }

    public get status(): RMA_STATUS | undefined { return this._model?.status; }
    public get statusTranslationKey(): RMA_STATUS_TRANSLATION_KEY | undefined { return this._model?._status; }
    public get isRestockValidated(): boolean|undefined { return this._model?.is_restock_validated; }
    public get rmaReturnMethod(): IReturnMethod | undefined { return this._model?._rma_return_method; }
    private get _orderService(): OrderService { return rootContainer.get<OrderService>(TYPE.OrderService); }
    // TODO: verify with product that grabbing status of latest shipment is OK
    public get shipmentStatus(): string { return this.shipmentList[this.shipmentList.length - 1]?.status ?? ''; }
    private get shipmentStatusCode(): SHIPMENT_STATUS_ENUM | null { return this._model?._overall_shipment_status ?? null; }
    public get returnMethodPresets(): IRMAReturnMethodPreset[] { return this._settings?.return_methods ?? []; }
    public get storeLocationsPresets(): IStoreLocation[] { return this._settings?.store_locations ? Object.values(this._settings?.store_locations) : []; }
    public get hasRestockPermission(): boolean { return this._settings?.has_restock_permission ?? false; }
    public get returnMethodOptions(): IOption[] {
        return this.returnMethodPresets.map(returnMethodPreset => {
            return {
                label: returnMethodPreset.internal_name,
                value: returnMethodPreset.id,
            }
        });
    }
    public get displayNewTimelineNote(): boolean { return this._displayNewTimelineNote; }
    public get newTimelineNote(): string { return this._newTimelineNote; }
    public get isValidatingItems(): boolean { return this._isValidatingItems; }
    public get shouldDisplayPaymentsExchangesCard(): boolean {
        return !!this.customerCaseStore?.exchangeOrderList.length ||
            !!this.customerCaseStore?.paymentsStoreList.filter(payment => !payment.isHidden).length;
    }
    public get shouldDisplayNewItemsCard(): boolean {
        return !!(
            this.customerCaseStore?.exchangesList?.length
        );

    }

    public get selectedProductCases(): ProductCaseStore[] {
        return this.customerCaseStore?.productCaseList.filter(pc => pc.isChecked) ?? [];
    }
    public get isRefunding(): boolean { return this._isRefunding; }
    public get defaultRefundLevel(): REFUND_LEVEL | undefined { return this._settings?.default_refund_level}
    public get createdAt(): string | undefined { return this._model?.created_at; }

    /** Global-E RMA identifiers row on data cards; requires capability and API payload */
    public get shouldShowGlobalERmaPayloadFields(): boolean {
        const cap = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.GLOBAL_E_RMA_PAYLOAD);
        if (cap !== IGlobalERmaPayloadCapability.ENABLED) {
            return false;
        }
        return this._model?.global_e_rma != null;
    }

    public get globalERmaPayload(): { return_id: string | null; order_id: string | null } | null {
        return this._model?.global_e_rma ?? null;
    }

    public get bulkRmaActionInProgress(): IRmaBulkRmaAction | null {
        return this._model?._bulk_rma_action ?? null;
    }
    private get _refundCalculatorService(): RefundCalculatorService { return rootContainer.get<RefundCalculatorService>(TYPE.RefundCalculatorService);}

    public get noShipmentRequiredReturnMethodId(): number | undefined {
        return this.returnMethodPresets.find(rm => rm.type === RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED)?.id
    }

    public get shouldReactOnReturnMethodChange(): boolean {
        const perShipmentFee = this.customerCaseStore?.returnLabelFee ?? 0;
        return perShipmentFee > 0 || !!this.customerCaseStore?.shouldConsiderCost;
    }

    public get isValidateAllEnabled(): boolean {
        return currentPortalStore.isFeatureAllowed(FEATURES.VALIDATE_ITEMS) &&
            this.shipmentStatusCode === SHIPMENT_STATUS_ENUM.RECEIVED &&
            !!currentPortalStore.setting?.isValidationEnabled &&
            !!currentPortalStore.setting?.isValidateAllEnabled &&
            !!this.customerCaseStore && this.customerCaseStore.notValidatedProductsCount > 0;
    }

    public get storeCurrency(): string { return this._settings?.store_currency ?? "USD"; }

    public dropOffLocationAddress(): IDropOffLocationAddress | undefined {
        if (this.rmaReturnMethod?.type !== RETURN_METHOD_TYPE.DROP_OFF_LOCATION) {
            return undefined;
        }
        if (this.rmaReturnMethod?.shopify_drop_off_location_id === DROP_OFF_LOCATION.CUSTOM_ADRESS && this.rmaReturnMethod.address?.shopify_location_id) {
            return {
                source: DROP_OFF_LOCATION.DYNAMIC_ADDRESS,
                name: this.rmaReturnMethod.address.name,
            }
        }

        if (this.rmaReturnMethod?.shopify_drop_off_location_id === DROP_OFF_LOCATION.CUSTOM_ADRESS && !this.rmaReturnMethod.address?.shopify_location_id) {
            return {
                source: DROP_OFF_LOCATION.CUSTOM_ADRESS,
                name: null,
            }
        }

        if (this.rmaReturnMethod?.shopify_drop_off_location_id === DROP_OFF_LOCATION.DROPOFF_INTEGRATION) {
            return {
                source: DROP_OFF_LOCATION.DROPOFF_INTEGRATION,
                name: null,
            }
        }
    }

    public storeLocationsOptions(getTranslation: (key: string, scope: string, params?: string[]) => string): GroupedOption[] {
        const rmaCardPopups = 'dashboard-rma-card-popups';
        const options: GroupedOption[] = [
            {
                category: getTranslation('DYNAMIC_LOCATION', rmaCardPopups),
                options: [
                    {
                        value: DROP_OFF_LOCATION.FULFILLMENT_LOCATION,
                        label: getTranslation('FULFILLMENT_LOCATION', rmaCardPopups),
                    }
                ],
            },
            {
                category: getTranslation('OTHER_LOCATIONS', rmaCardPopups),
                options: this.storeLocationsPresets.filter(o => !o.fulfillsOnlineOrders).map(o => ({
                    value: o.location_id,
                    label: o.name,
                }))
            },
            {
                category: getTranslation('ONLINE_INVENTORY', rmaCardPopups),
                options: this.storeLocationsPresets.filter(o => o.fulfillsOnlineOrders).map(o => ({
                    value: o.location_id,
                    label: o.name,
                }))
            },
        ];

        return options;
    }

    public validationStatusChipData(): {validationStatusTextTranslationKey: string, validationStatusTextTranslationScope: string, validationChipColor: CHIP_COLORS} | undefined {
        /* If not allowed or not enabled */
        if(!(currentPortalStore.isFeatureAllowed(FEATURES.VALIDATE_ITEMS) && currentPortalStore.setting?.isValidationEnabled)){
           return undefined;
        }
        /* Everything is validated */
        if(this._model?.is_restock_validated){
            return {
                validationStatusTextTranslationKey: 'ITEMS_VALIDATED',
                validationStatusTextTranslationScope: 'dashboard-returns-rma-card',
                validationChipColor: CHIP_COLORS.VALIDATED,
            }
        }

        let shouldBeValidatedItems:number = 0;
        let validatedItems:number = 0;
        this.customerCaseStore?.productCaseList.forEach(pc => {
            if ((!pc.isValidated && pc.requiresShipping && pc.wasReceived())) {
                shouldBeValidatedItems++;
            }
            if (pc.isValidated) {
                validatedItems++;
            }
        });
        /* Else if all items that can be validated aren't */
        if(shouldBeValidatedItems > 0 && validatedItems === 0){
            return {
                validationStatusTextTranslationKey: 'ITEMS_PENDING_VALIDATION',
                validationStatusTextTranslationScope: 'dashboard-returns-rma-card',
                validationChipColor: CHIP_COLORS.ORANGE,
            }
        }
        /* Else if some items that can be validated aren't */
        if(shouldBeValidatedItems > 0 && validatedItems > 0){
            return {
                validationStatusTextTranslationKey: 'ITEMS_PARTIALLY_VALIDATED',
                validationStatusTextTranslationScope: 'dashboard-returns-rma-card',
                validationChipColor: CHIP_COLORS.ORANGE,
            };
        }
        /* No items that need validation */
        return undefined;
    }

    public get refundCalculatorStore(): RefundCalculatorStore|undefined { return this._refundCalculatorStore; }


    showNewTimelineNote() {
        return this._displayNewTimelineNote = true;
    }
    discardNewTimelineNote() {
        this._displayNewTimelineNote = false;
        this._newTimelineNote = '';
    }
    setNewTimelineNote(value: string){
        this._newTimelineNote = value;
    }
    public async validateItemsAction(validationRequestModel: IValidateItemsActionRequest){
        try {
            this._isValidatingItems = true;
            const response = await this._service.saveValidation(this._rmaId, validationRequestModel);
            runInAction(() => {
                this._isValidatingItems = false;
                this._setModel(response);
                this._mainStore.addUpdatedRmaId(this._rmaId);
            });
        }
        catch(e){
            runInAction(() => this._isValidatingItems = false);
            // Re-fetch RMA and update UI - even though backend throws exception - there may be
            // additional changes in the RMA data (e.g., return errors in the timeline, new shipments, etc.)
            this.loadRma();
            console.log('Error validating items', e);
        }
    }

    public async _validateAllItems(){
        try {
            this._isValidatingItems = true;
            const response = await this._service.saveValidationAll(this._rmaId);
            runInAction(() => {
                this._isValidatingItems = false;
                this._setModel(response);
                this._mainStore.addUpdatedRmaId(this._rmaId);
            });
        }
        catch(e){
            runInAction(() => this._isValidatingItems = false);
            // Re-fetch RMA and update UI - even though backend throws exception - there may be
            // additional changes in the RMA data (e.g., return errors in the timeline, new shipments, etc.)
            this.loadRma();
            console.log('Error validating all items', e);
        }
    }

    private constructRmaActions(): void {
        const thisStore = this;

        this._rmaAactions[RMA_ACTION_TYPE.APPROVE] = {
            type: RMA_ACTION_TYPE.APPROVE,
            name: 'APPROVE_ONE_WORD',
            modalTitleTranslationKey: 'APPROVE_RETURN',
            descriptionTranslationKey: 'ARE_YOU_SURE_YOU_WANT_TO_APPROVE_THIS_RETURN_REQUEST',
            buttonTextTranslationKey: 'APPROVE_RETURN',
            store: new RmaApproveActionStore(thisStore, (data: IRmaApproveRequest) => thisStore._approve(data)),
            get disabled() { return thisStore._isApproveDisabled(); },
        };

        this._rmaAactions[RMA_ACTION_TYPE.RECEIVE_SHIPMENT] = {
            type: RMA_ACTION_TYPE.RECEIVE_SHIPMENT,
            name: 'SHIPMENT_RECEIVED',
            modalTitleTranslationKey: 'RECEIVE_SHIPMENT',
            descriptionTranslationKey: 'ARE_YOU_SURE_YOU_WANT_TO_MARK_THIS_SHIPMENT_AS_RECEIVED',
            buttonTextTranslationKey: 'SHIPMENT_RECEIVED',
            store: new ReceiveShipmentActionStore(thisStore, (data: IReceiveShipmentRequest) => thisStore._receiveShipment(data)),
        };

        this._rmaAactions[RMA_ACTION_TYPE.MARK_AS_DONE] = {
            type: RMA_ACTION_TYPE.MARK_AS_DONE,
            name: 'MARK_AS_DONE',
            modalTitleTranslationKey: 'MARK_AS_DONE',
            descriptionTranslationKey: 'ARE_YOU_SURE_YOU_WANT_TO_MARK_THIS_SHIPMENT_AS_DONE',
            buttonTextTranslationKey: 'MARK_AS_DONE',
            store: new RmaActionStore(() => thisStore._markAsDone()),
            get disabled() { return !thisStore._canMarkAsDone(); },
        };

        this._rmaAactions[RMA_ACTION_TYPE.REOPEN] =  {
            type: RMA_ACTION_TYPE.REOPEN,
            name: 'REOPEN_RETURN',
            descriptionTranslationKey: 'ARE_YOU_SURE_YOU_WANT_TO_REOPEN_THIS_RETURN_REQUEST',
            buttonTextTranslationKey: 'REOPEN',
            store: new RmaReopenActionStore(thisStore, (data: IRmaReopenReuest) => thisStore._reopen(data)),
        }

        this._rmaAactions[RMA_ACTION_TYPE.REJECT] = {
            type: RMA_ACTION_TYPE.REJECT,
            name: 'REJECT_RETURN',
            descriptionTranslationKey: 'ARE_YOU_SURE_YOU_WANT_TO_REJECT_THIS_RETURN_REQUEST',
            buttonTextTranslationKey: 'REJECT_RETURN',
            store: new RmaRejectActionStore(thisStore, (data: IRmaRejectRequest) => thisStore._reject(data)),
        }

        this._rmaAactions[RMA_ACTION_TYPE.REJECT_ITEM] = {
            type: RMA_ACTION_TYPE.REJECT_ITEM,
            name: '',
            descriptionTranslationKey: '',
            buttonTextTranslationKey: '',
            store: new RmaRejectItemActionStore(thisStore, (data: IRmaRejectItemRequest) => thisStore._rejectItem(data)),
        }

        /** Opened from a timeline error row, so it carries no toolbar button copy. */
        this._rmaAactions[RMA_ACTION_TYPE.RESOLVE_LABEL_ERROR] = {
            type: RMA_ACTION_TYPE.RESOLVE_LABEL_ERROR,
            name: '',
            descriptionTranslationKey: '',
            buttonTextTranslationKey: '',
            store: new ResolveLabelErrorActionStore(thisStore),
        }

        this._rmaAactions[RMA_ACTION_TYPE.CANCEL] = {
            type: RMA_ACTION_TYPE.CANCEL,
            name: 'CANCEL_RETURN',
            descriptionTranslationKey: 'ARE_YOU_SURE_YOU_WANT_TO_CANCEL_THIS_RETURN_REQUEST',
            buttonTextTranslationKey: 'CANCEL_RETURN',
            store: new RmaCancelActionStore(thisStore, (data?: IRmaCancelRequest) => thisStore._cancel(data)),
        };

        this._rmaAactions[RMA_ACTION_TYPE.RELEASE_EXCHANGE] = {
            type: RMA_ACTION_TYPE.RELEASE_EXCHANGE,
            name: 'RELEASE_EXCHANGE',
            descriptionTranslationKey: '',
            buttonTextTranslationKey: 'RELEASE_EXCHANGE',
            store: new RmaActionStore(() => thisStore._releaseExchange()),
            get disabled() { return thisStore.isReleaseExchangeDisabled(); },
        };

        this._rmaAactions[RMA_ACTION_TYPE.RESTOCK] = {
            type: RMA_ACTION_TYPE.RESTOCK,
            name: 'RESTOCK',
            descriptionTranslationKey: 'CHOOSE_THE_INVENTORY_LOCATION_FOR',
            buttonTextTranslationKey: 'RESTOCK',
            store: new RmaRestockActionStore(thisStore, (data: IRestockRequest) => thisStore._restock(data)),
            get disabled() { return thisStore.isRestockDisabled(); },
        };

        this._rmaAactions[RMA_ACTION_TYPE.RESOLVE_ERRORS] = {
            type: RMA_ACTION_TYPE.RESOLVE_ERRORS,
            name: 'PLEASE_CONFIRM',
            descriptionTranslationKey: 'ARE_YOU_SURE_YOU_WANT_TO_MARK_THIS_ISSUE_AS_RESOLVED',
            buttonTextTranslationKey: 'APPROVE_ONE_WORD',
            store: new RmaActionStore(() => thisStore._resolveErrors()),
        };

        const refundCallback = () => {
            runInAction(() => {
                thisStore._refundCalculatorStore = new RefundCalculatorStore();
                return thisStore._refundCalculatorStore.load(thisStore.rmaId, thisStore.selectedProductCases, thisStore.customerCaseStore);
            })
        };
        this._rmaAactions[RMA_ACTION_TYPE.REFUND] = {
            type: RMA_ACTION_TYPE.REFUND,
            name: 'Order Refund',
            descriptionTranslationKey: '',
            buttonTextTranslationKey: 'REFUND_OR_CREDIT',
            store: new RmaActionStore(refundCallback),
        };

        this._rmaAactions[RMA_ACTION_TYPE.VALIDATE_ALL_ITEMS] = {
            type: RMA_ACTION_TYPE.VALIDATE_ALL_ITEMS,
            name: 'VALIDATE_ALL',
            descriptionTranslationKey: 'VALIDATE_ALL_DESCRIPTION',
            buttonTextTranslationKey: 'VALIDATE_ALL_BUTTON',
            store: new RmaActionStore(thisStore._validateAllItems),
        };
    }

    public nextAction(): IRmaApproveAction | IRmaReceiveShipmentAction | IRmaMarkAsDoneAction | null {
        switch (this.status) {
            case RMA_STATUS.PENDING_APPROVAL:
                return this._rmaAactions[RMA_ACTION_TYPE.APPROVE];
            case RMA_STATUS.APPROVED:
                const notReceivedYetStatuses: (SHIPMENT_STATUS_ENUM|null)[] = [
                    SHIPMENT_STATUS_ENUM.PENDING_SHIPMENT, SHIPMENT_STATUS_ENUM.LABEL_CREATED, SHIPMENT_STATUS_ENUM.SHIPPED, SHIPMENT_STATUS_ENUM.OUT_FOR_DELIVERY
                ];
                const receivedStatuses: (SHIPMENT_STATUS_ENUM|null)[] = [
                    SHIPMENT_STATUS_ENUM.RECEIVED, SHIPMENT_STATUS_ENUM.VERIFIED
                ];

                if (this.shipmentStatusCode === SHIPMENT_STATUS_ENUM.REJECTED || notReceivedYetStatuses.includes(this.shipmentStatusCode)) {
                    return this._rmaAactions[RMA_ACTION_TYPE.RECEIVE_SHIPMENT];
                }
                else if (this.rmaReturnMethod?.type === RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED || receivedStatuses.includes(this.shipmentStatusCode)) {
                    return this._rmaAactions[RMA_ACTION_TYPE.MARK_AS_DONE];
                }
        }
        return null;
    }

    public rejectAction(): IRmaRejectAction | null {
        /** If we do not know rma status or status is Done or Rejected - we do not allow to reject */
        if (this.status === undefined || [RMA_STATUS.DONE, RMA_STATUS.REJECTED].includes(this.status)) {
            return null;
        }

        return this._rmaAactions[RMA_ACTION_TYPE.REJECT];
    }

    public rejectItemAction(): IRmaRejectItemAction | null
    {
        return this._rmaAactions[RMA_ACTION_TYPE.REJECT_ITEM];
    }

    public resolveLabelErrorAction(): IRmaResolveLabelErrorAction {
        return this._rmaAactions[RMA_ACTION_TYPE.RESOLVE_LABEL_ERROR];
    }

    public reopenAction(): IRmaReopenAction | null {
        /**
         * We do not allow to reopen if
         * - do not know rma status
         * - status is Done, Rejected or Cancelled already
         * - rma is not cancelled
         * */
        if (
            this.status === undefined
            || [RMA_STATUS.DONE, RMA_STATUS.REJECTED].includes(this.status)
            || this.status !== RMA_STATUS.CANCELED
        ) {
            return null;
        }
        return this._rmaAactions[RMA_ACTION_TYPE.REOPEN];
    }

    public cancelAction(): IRmaCancelAction | null {
        /**
         * We do not allow to cancel if
         * - do not know rma status
         * - status is Done, Rejected or Cancelled already
         * */
        if (
            this.status === undefined
            || [RMA_STATUS.DONE, RMA_STATUS.REJECTED, RMA_STATUS.CANCELED].includes(this.status)
        ) {
            return null;
        }

        return this._rmaAactions[RMA_ACTION_TYPE.CANCEL];
    }

    public releaseExchangeAction(): IRmaReleaseExchangeAction | null {
        /** If there is no draft order - do not show the button at all */
        if (!this._getDraftExchangeOrder()) {
            return null;
        }

        return this._rmaAactions[RMA_ACTION_TYPE.RELEASE_EXCHANGE];
    }

    public restockAction(): IRmaRestockAction | null {
        return this._rmaAactions[RMA_ACTION_TYPE.RESTOCK];
    }

    public resolveErrorsAction(): IRmaResolveErrorsAction | null {
        return this._rmaAactions[RMA_ACTION_TYPE.RESOLVE_ERRORS];
    }

    public isRestockDisabled(): boolean {
        if (!this.hasRestockPermission) {
            return true;
        }
        if ((this._customerCaseStore?.isCancelResolution() ?? false)) {
            return true;
        }
        /** order level refund - restock is desabled */
        if (this.selectedProductCases.length === 0) {
            return true;
        }
        /** when all selected items are not restockable - disable the button */
        return this.selectedProductCases.every(pc => !pc._isRestockable);
    }

    public isReleaseExchangeDisabled(): boolean {
        const draftExchangeOrder = this._getDraftExchangeOrder();
        return !draftExchangeOrder || draftExchangeOrder.internalState !== DRAFT_ORDER_STATE.DRAFT_ORDER_CREATED;
    }

    private _getDraftExchangeOrder(): RmaExchangeOrderStore | undefined {
        return this.customerCaseStore?.exchangeOrderList.find(eo => DRAFT_ORDER_EXCHANGE_TYPES.includes(eo.draftOrderType));
    }

    public getNoneRestockableSelectedItemsCount(): number {
        return this.selectedProductCases.filter(pc => !pc._isRestockable).length;
    }

    public rmaActionsToolbarStore(): RmaActionsToolbarStore {
        return this._rmaActionsStore;
    }

    public refundAction(): IRmaRefundAction | null {
        return this._rmaAactions[RMA_ACTION_TYPE.REFUND];
    }

    public validateAllAction(): IRmaValidateAllAction | null {
        if (!this.isValidateAllEnabled) {
            return null;
        }

        return this._rmaAactions[RMA_ACTION_TYPE.VALIDATE_ALL_ITEMS];
    }

    public shouldRequestManagersApproval(): boolean {
        if (!this._model || !currentPortalStore.requireManagerApproval()) {
            return false;
        }

        if (!this._model.required_manager_approval || this.status !== RMA_STATUS.PENDING_APPROVAL) {
            return false;
        }

        if (currentAccountStore.hasPermission(PERMISSION.ALL) || currentAccountStore.hasPermission(PERMISSION.APPROVE_RMA_AS_MANAGER)) {
            return false;
        }

        return currentAccountStore.hasPromptManagersApprovePermissions();
    }

    private _isApproveDisabled(): boolean {
        if (this._model?.required_manager_approval) {
            if (currentPortalStore.requireManagerApproval()) {
                if (!currentAccountStore.hasPromptManagersApprovePermissions() && !currentAccountStore.hasManagersApprovePermissions()) {
                    return true;
                }
            }
            else if (!currentAccountStore.hasManagersApprovePermissions()) {
                return true;
            }
        }

        if (
            this.customerCaseStore?.feeOrder
            && [DRAFT_ORDER_STATE.PENDING_INVOICE, DRAFT_ORDER_STATE.PENDING_PAYMENT].includes(this.customerCaseStore?.feeOrder.internalState)
        ) {
            return true;
        }

        return false;
    }

    public isOutOfPolicyRma(): boolean {
        return this._model?.is_out_of_policy ?? false;
    }

    public setManagerCredentials(managerCredentials: IManagerCredentials | undefined) {
        this._managerCredentials = managerCredentials;
    }

	/** RMA actions are performed from relevant action store which handles isProcessing property */
	private async _approve(data?: IRmaApproveRequest) {
        /** Should never happen */
        if (!data) {
            throw new Error('No data provided!');
        }

        try {
            const managerCredentials = this._managerCredentials;
            this._managerCredentials = undefined;
            if (managerCredentials) {
                data.manager_approval_credentials = managerCredentials;
            }

			const response = await this._service.approve(this._rmaId, data);
			runInAction(() => {
                this._setModel(response);
                this._mainStore.addUpdatedRmaId(this._rmaId);
                /* This doesn't seem self explanatory - In RMAs that were created via Resolution of type Cancel (Cancel RMA),
                when Approve is performed - the order is cancelled on the side of Shopify/Magento/younameit so we want to
                re-fetch order and update the store so that UI will be updated accordingly (think of "Cancelled" label in order card)

                @TODO: find a better solution that would be self-explantory
                */
                if (this.customerCaseStore?.isCancelResolution()) {
                    this._loadOrder();
                }
			});
		}
		catch(e) {
            // Re-fetch RMA and update UI - even though backend throws exception - there may be
            // additional changes in the RMA data (e.g., return errors in the timeline, new shipments, etc.)
            this.loadRma();

			console.log('Error approving rma', e);
		}
    }

    private async _receiveShipment(data?: IReceiveShipmentRequest) {
        /** Should never happen */
        if (!data) {
            throw new Error('No data provided!');
        }

        try {
			const response = await this._service.receiveShipment(this._rmaId, data);
			runInAction(()=>{
				this._setModel(response);
                this._mainStore.addUpdatedRmaId(this._rmaId);
			})
		}
		catch(e){
            // Re-fetch RMA and update UI - even though backend throws exception - there may be
            // additional changes in the RMA data (e.g., return errors in the timeline, new shipments, etc.)
            this.loadRma();

			console.log('Error confirming that shipment is received', e);
		}
    }

    private async _reopen(data?: IRmaReopenReuest) {
        /** Should never happen */
        if (!data) {
            throw new Error('No data provided!');
        }

        try {
			const response = await this._service.reopen(this._rmaId, data);
            runInAction(()=>{
				this._setModel(response);
                this._mainStore.addUpdatedRmaId(this._rmaId);
			})
		}
		catch(e){
            // Re-fetch RMA and update UI - even though backend throws exception - there may be
            // additional changes in the RMA data (e.g., return errors in the timeline, new shipments, etc.)
            this.loadRma();

            console.log('Error reopening rma', e);
        }
    }

    private async _reject(data?: IRmaRejectRequest) {
        /** Should never happen */
        if (!data) {
            throw new Error('No data provided!');
        }

        try {
			const response = await this._service.reject(this._rmaId, data);
            runInAction(()=>{
				this._setModel(response);
                this._mainStore.addUpdatedRmaId(this._rmaId);
			})
		}
		catch(e){
            // Re-fetch RMA and update UI - even though backend throws exception - there may be
            // additional changes in the RMA data (e.g., return errors in the timeline, new shipments, etc.)
            this.loadRma();

            console.log('Error rejecting rma', e);
        }
    }

    private async _rejectItem(data?: IRmaRejectItemRequest): Promise<void> {

        if(!data) {
            throw new Error('No data provided!');
        }

        try {
            const rma = await this._service.rejectItem(this._rmaId, data);
            runInAction(() => {
                this._setModel(rma);
                this._mainStore.addUpdatedRmaId(this._rmaId);
            });
        }
        catch (e) {
            this.loadRma();
            console.log('Error cancelling rma', e);
        }
    }

    private async _cancel(data?: IRmaCancelRequest) {
        /** Should never happen */
        if (!data) {
            throw new Error('No data provided!');
        }

        try {
			const response = await this._service.cancel(this._rmaId, data);
            runInAction(()=>{
				this._setModel(response);
                this._mainStore.addUpdatedRmaId(this._rmaId);
			})
		}
		catch(e){
            // Re-fetch RMA and update UI - even though backend throws exception - there may be
            // additional changes in the RMA data (e.g., return errors in the timeline, new shipments, etc.)
            this.loadRma();

            console.log('Error cancelling rma', e);
        }
    }

    private async _markAsDone() {
        try {
			const response = await this._service.markAsDone(this._rmaId);
			runInAction(()=>{
				this._setModel(response);
                this._mainStore.addUpdatedRmaId(this._rmaId);
			})
		}
		catch(e){
            // Re-fetch RMA and update UI - even though backend throws exception - there may be
            // additional changes in the RMA data (e.g., return errors in the timeline, new shipments, etc.)
            this.loadRma();

			console.log('Error trying to mark RMA as done', e);
		}
    }

    private async _resolveErrors() {
        try {
			const response = await this._service.resolveErrors(this._rmaId);
			runInAction(()=>{
				this._setModel(response);
                this._mainStore.addUpdatedRmaId(this._rmaId);
			})
		}
		catch(e){
            // Re-fetch RMA and update UI - even though backend throws exception - there may be
            // additional changes in the RMA data (e.g., return errors in the timeline, new shipments, etc.)
            this.loadRma();

			console.log('Error trying to resolve RMA errors', e);
		}
    }

    public async resendStatusEmail(emailLogId: number) {
        this._isResendingStatusEmail = true;
        try {
			const response = await this._service.resendStatusEmail(this._rmaId, emailLogId);
            runInAction(()=>{
                this._isResendingStatusEmail = false;
                this._setModel(response);
			})
		}
		catch(e){
            runInAction(()=>{
                this._isResendingStatusEmail = false;
			})

            // Re-fetch RMA and update UI - even though backend throws exception - there may be
            // additional changes in the RMA data (e.g., return errors in the timeline, new shipments, etc.)
            this.loadRma();

            console.log('Error resending rma status email', e);
        }
    }

    private async _releaseExchange() {
        this._isReleasingExchnage = true;
        try {
			const response = await this._service.releaseExchange(this._rmaId);
			runInAction(()=>{
                this._isReleasingExchnage = false;
				this._setModel(response);
                this._mainStore.addUpdatedRmaId(this._rmaId);
			});
		}
		catch(e){
			runInAction(()=>{
                this._isReleasingExchnage = false;
			});

            // Re-fetch RMA and update UI - even though backend throws exception - there may be
            // additional changes in the RMA data (e.g., return errors in the timeline, new shipments, etc.)
            this.loadRma();

			console.log('Error trying release exchange', e);
		}
    }
    private async _restock(data?: IRestockRequest) {
        /** Should never happen */
        if (!data) {
            throw new Error('No data provided!');
        }

        try {
			const response = await this._service.restock(this._rmaId, data);
			runInAction(()=>{
				this._setModel(response);
                this._mainStore.addUpdatedRmaId(this._rmaId);
			});
		}
		catch(e){
            // Re-fetch RMA and update UI - even though backend throws exception - there may be
            // additional changes in the RMA data (e.g., return errors in the timeline, new shipments, etc.)
            this.loadRma();

			console.log('Error trying restock', e);
		}
    }

    public async addNote() {
        try {
            this._isSavingNewNote = true;
            const response = await this._service.addNote(this._rmaId, {note: this._newTimelineNote });
            runInAction(()=>{
                this._setTimelineModel(response);
                this.discardNewTimelineNote();
                this._isSavingNewNote = false;
            })
        }
        catch(e){
            runInAction(() => this._isSavingNewNote = false);
            console.log('Error trying to add note', e);
        }
    }

	public async loadRma(){
		this._isLoading = true;
		try {
			const response = await this._service.fetchRma(this._rmaId);
			runInAction(()=>{
				this._setModel(response);
				this._isLoading = false;
                this._loadOrder();
                const excludeShopperProfile = currentAccountStore.isSysAdmin;
                customersMainStore.fetchCustomersByIds([this.customerCaseStore?.customerId!], excludeShopperProfile);
                customersMainStore.getCustomerStore(this.customerCaseStore?.customerId!)?.loadRmasList();
			})
		}
		catch(e){
			console.log('Error fetching rma', e);
			runInAction(() => {
				this._isLoading = false;
			});
		}
	}

    private _canMarkAsDone(): boolean {
        if (!currentPortalStore.setting?.isValidationEnabled) {
            return true;
        }
        if (currentPortalStore.setting?.isRestockNoteMandatory && this._shipmentList.length > 0) {
            return this.isRestockValidated ?? false;
        }
        return true;
    }

	private _setModel(response: IRmaData){
		this._model = ensureValidModel(response.rma);
		this._settings = response.settings;
        this._shipmentList = response.rma.shipments.map(shipment => new RmaShipmentStore(this, shipment));
        this._customerCaseStore = new CustomerCaseStore(response.rma.customer_case, this);
        this._errorListStore = new RmaErrorListStore(response.rma.return_errors);
        this._setTimelineModel({timeline: response.rma.timeline});
        this._emailLogList = response.rma._email_logs;
        this._aggregatorsInfoStore = new AggregatorsInfoStore(this._settings?.aggregators_info);
	}

    private _setTimelineModel(response: Pick<IRma, 'timeline'>){
        this._timelineListManager = new TimelineManagerStore(response.timeline);
    }

    private async _loadOrder() {
        this._isOrderLoading = true;
        try {
            if (this.customerCaseStore?.orderName === undefined) {
                throw new Error('Order name is undefined, can\'t load order');
            }
            const orderName = this.customerCaseStore?.orderName;
            const globalEOrderId = this.customerCaseStore?.globalEOrderId;

            const withEligibility = false;
            const useCache = false;
            const withRefunded = true;
            const excludeShopperProfile = currentAccountStore.isSysAdmin;
            const response = await this._orderService.fetchOrder(
                orderName,
                globalEOrderId,
                withEligibility,
                useCache,
                withRefunded,
                false,
                excludeShopperProfile,
            );
            runInAction(() => {
                this._orderStore = new OrderStore(response, undefined);
				this._isOrderLoading = false;
                this._orderStore.loadRmasList();
            })
        }
        catch(e){
            console.log('Error fetching order', e);
            runInAction(()=>{
                this._isOrderLoading = false;
            })
        }
    }

    public async refund(closeDrawer: ()=>void): Promise<void> {
        this._isRefunding = true;
        const dto = this.refundCalculatorStore?.buildRefundRequestDto();
        if (!dto) {
            return;
        }

        try {
            const response = await this._refundCalculatorService.refund(dto, this.rmaId);
            runInAction(() => {
                this._isRefunding = false;
                this._setModel(response);
                this._mainStore.addUpdatedRmaId(this._rmaId);
                closeDrawer();
            } )
        }
        catch(e) {
            runInAction(() => {
                this._isRefunding = false;
            });
            // Re-fetch RMA and update UI - even though backend throws exception - there may be
            // additional changes in the RMA data (e.g., return errors in the timeline, new shipments, etc.)
            this.loadRma();
            console.error('Failed to refund rma: ', e);
        }

    }

    /**
     * Returns true if the user can perform a refund on this RMA.
     * When RefundsCapability.blockRefundWhenPending is true, refund is blocked when status is PENDING_APPROVAL.
     */
    public isRefundAllowed(): boolean {
        if (!currentAccountStore.hasPermission(PERMISSION.REFUND_RMA)) {
            return false;
        }
        const refundsCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.REFUNDS);
        if (refundsCapability?.blockRefundWhenPending && this.status === RMA_STATUS.PENDING_APPROVAL) {
            return false;
        }
        return true;
    }

    public isCoverageReturnMethod(): boolean {
        /** for RC-RM (Coverage RM) we don't want to show service level auto-select-cheapest (request from Nadav) */
        const aggCode =this.rmaReturnMethod?.return_label_setting?.shipping_connection?.aggregator_code;
        if (!aggCode) {
            return false;
        }
        return this._aggregatorsInfoStore!.isCoverage(aggCode);
    }

}
export default RmaPageStore;

function ensureValidModel(model: IRma): IRma {
    const toBool = (v: undefined | any) => Boolean(v);
    return {
        ...model,
        required_manager_approval: toBool(model.required_manager_approval),
        is_out_of_policy: toBool(model.is_out_of_policy)
    }
}
